import type { RankType } from '../api/contests';

const CHIP_COLORS = ['default', 'primary', 'success', 'warning', 'secondary', 'error'] as const;
export type RankChipColor = (typeof CHIP_COLORS)[number];

function stableColorFromString(s: string): RankChipColor {
  // simple deterministic hash -> palette
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(h) % CHIP_COLORS.length;
  return CHIP_COLORS[idx];
}

export function normalizeRankType(type: RankType): string {
  const t = String(type ?? '').trim();

  // Treat empty/placeholder values as "no rank".
  if (!t || t === '0' || t === '—' || t.toLowerCase() === 'none' || t.toLowerCase() === 'null') return '';

  // Collapse legacy naming into the new one for consistent display and counting.
  if (t === '1Р') return '1РЗ';
  return t;
}

export function rankDisplayLabel(type: RankType): string {
  return normalizeRankType(type);
}

export function rankTitle(type: RankType): string {
  const t = String(type);

  // Prefer explicit known labels
  if (t === 'КМЗ') return 'Кандидат в мастера заруб';
  if (t === 'МЗ') return 'Мастер заруб';
  if (t === 'МЗМК') return 'Мастер заруб международного класса';
  if (t === 'ЛЗ') return 'Легенда заруб';

  // Generic “RyZ” ranks (e.g., 1РЗ/2РЗ/3РЗ)
  const m = t.match(/^(\d+)РЗ$/);
  if (m) return `${m[1]} разряд заруб`;

  // Legacy
  if (t === '1Р') return '1 разряд заруб';

  return t;
}

export function rankChipColor(type: RankType): RankChipColor {
  const t = String(type);

  // Keep a nice familiar palette for main titles
  if (t === 'КМЗ') return 'primary';
  if (t === 'МЗ') return 'success';
  if (t === 'МЗМК') return 'warning';
  if (t === 'ЛЗ') return 'secondary';

  // All “разряд заруб” (including 1РЗ/2РЗ/3РЗ) default color
  if (/^\d+РЗ$/.test(t) || t === '1Р') return 'default';

  // Unknown future rank types: stable deterministic color
  return stableColorFromString(t);
}

export function rankPriority(type: RankType): number {
  const t = String(type);

  // Higher number => better rank
  if (t === 'ЛЗ') return 1000;
  if (t === 'МЗМК') return 900;
  if (t === 'МЗ') return 800;
  if (t === 'КМЗ') return 700;

  // 1РЗ/2РЗ/3РЗ...: bigger number => better
  const m = t.match(/^(\d+)РЗ$/);
  if (m) return 100 + Number(m[1]);

  // legacy 1Р: treat as 1РЗ
  if (t === '1Р') return 101;

  // Unknown future rank types: keep them after known ones but stable
  return 0;
}
